// Plain-words labels for station-ops' eRound provisioning records (SOPS-148),
// per specs/013-eround-provisioning-review/data-model.md. An unknown server
// value is shown verbatim, so a new status never breaks the page.

import { WaitingOn } from "../types/eroundProvisioning";

// PrimeReact's Tag has no neutral severity; null marks the neutral statuses,
// which ProvisioningStatusTag renders in the surface colour.
export type TagSeverity = "success" | "info" | "warning" | "danger" | null;

const STATUS: Record<string, { label: string; severity: TagSeverity }> = {
  NOT_TRACKED: { label: "Not tracked – manual eRound setup", severity: null },
  COLLECTING: { label: "Waiting for data", severity: null },
  AWAITING_APPROVAL: { label: "Ready for approval", severity: "warning" },
  DISPATCHED: { label: "Sent to eRound", severity: "info" },
  IN_PROGRESS: { label: "In progress in eRound", severity: "info" },
  SUCCEEDED: { label: "In eRound", severity: "success" },
  FAILED: { label: "Failed", severity: "danger" },
  NEEDS_MANUAL_ACTION: { label: "Needs manual action", severity: "danger" },
  REJECTED: { label: "Rejected – manual eRound setup", severity: null },
  CANCELLED: { label: "Withdrawn", severity: null },
  SUPERSEDED: { label: "Replaced", severity: null },
};

// Who resolves each gap mirrors station-ops' MissingPiece (SOPS-165): support
// can act now, or the customer still has to sign or provide data.
const MISSING: Record<string, { label: string; waitingOn: WaitingOn }> = {
  CONTRACT_NOT_SIGNED: { label: "Contract not signed yet", waitingOn: "CUSTOMER" },
  CREDENTIALS: { label: "OCPP credentials not saved yet", waitingOn: "SUPPORT" },
  STATION_NAME_INVALID: {
    label: "Station name isn't valid for eRound (3–19 capital letters or digits)",
    waitingOn: "SUPPORT",
  },
  ADDRESS_STREET: { label: "Station street missing", waitingOn: "CUSTOMER" },
  ADDRESS_POSTCODE: { label: "Station postcode missing", waitingOn: "CUSTOMER" },
  ADDRESS_COUNTRY: { label: "Station country missing or not recognised", waitingOn: "CUSTOMER" },
  LOCATION_TYPE: { label: "Location type missing", waitingOn: "CUSTOMER" },
  LOCATION_TYPE_UNMAPPED: { label: "Location type has no eRound equivalent", waitingOn: "SUPPORT" },
  WALLBOX_MODEL: { label: "Wallbox model missing", waitingOn: "CUSTOMER" },
  MODEL_NOT_MAPPED: { label: "Wallbox model not mapped to eRound yet", waitingOn: "SUPPORT" },
  CHARGING_POINTS: { label: "Number of charging points unknown", waitingOn: "CUSTOMER" },
  CHARGING_POINTS_MISMATCH: {
    label: "Number of charging points differs from the mapped model's",
    waitingOn: "SUPPORT",
  },
};

const WAITING: Record<string, { label: string; severity: TagSeverity }> = {
  SUPPORT: { label: "Waiting for support", severity: "warning" },
  CUSTOMER: { label: "Waiting for customer", severity: null },
};

const waiting = (status: string, waitingOn?: string | null) =>
  (status === "COLLECTING" && waitingOn && WAITING[waitingOn]) || null;

const MANUAL_ACTION: Record<string, string> = {
  KEY_ROTATED:
    "The OCPP key was regenerated after the station was created in eRound. Update the key in eRound by hand.",
  CHANGED_AFTER_PROVISIONING:
    "Station data changed after it was created in eRound. Apply the change in eRound by hand.",
  REMOVED_AFTER_PROVISIONING:
    "The station was removed from the prospect after it was created in eRound. Remove or reassign it in eRound by hand.",
};

/** A status in plain words; a station still collecting data says who it waits on. */
export const statusLabel = (status: string, waitingOn?: string | null): string =>
  waiting(status, waitingOn)?.label ?? STATUS[status]?.label ?? status;

export const statusSeverity = (status: string, waitingOn?: string | null): TagSeverity => {
  const wait = waiting(status, waitingOn);
  if (wait) return wait.severity;
  return status in STATUS ? STATUS[status].severity : null;
};

export const missingLabel = (piece: string): string => MISSING[piece]?.label ?? piece;

export const missingWaitingOn = (piece: string): WaitingOn | null => MISSING[piece]?.waitingOn ?? null;

export const manualActionGuidance = (reason: string | null): string =>
  (reason && MANUAL_ACTION[reason]) ||
  "This station needs to be handled in eRound by hand.";

/** Statuses the worker is still working on — the only ones worth refreshing. */
export const isInFlight = (status: string): boolean =>
  status === "DISPATCHED" || status === "IN_PROGRESS";

export const canApprove = (record: { status: string; fingerprint: string | null }): boolean =>
  record.status === "AWAITING_APPROVAL" && Boolean(record.fingerprint);

export const canRetry = (record: { status: string }): boolean => record.status === "FAILED";

// The eRound screens the automation checks (SOPS-164), by contract id.
const SCREEN_TITLES: Record<string, string> = {
  "auth.login": "Sign-in",
  "locations.overview": "Locations · List",
  "location_wizard.basic_data": "Location wizard · Basic data",
  "location_wizard.address": "Location wizard · Address",
  "location_wizard.preview": "Location wizard · Preview",
  "location_wizard.created": "Location wizard · Created",
  "stations.administration": "Stations · List",
  "station_create.location_selection": "New station · Location",
  "station_create.manufacturers": "New station · Manufacturer",
  "station_create.models": "New station · Model",
  "station_wizard.basic_data": "Station wizard · Basic data",
  "station_wizard.charging_points": "Station wizard · Charging points",
  connector_editor: "Connector editor",
  "station_wizard.review": "Station wizard · Review",
  "station_record.charging_points": "Saved station · Charging points",
  "station_record.review": "Saved station · Review",
  "station_settings.overview": "Station settings",
  "station_settings.ocpp_editor": "Station settings · OCPP editor",
};

export const screenTitle = (screen: string): string => SCREEN_TITLES[screen] ?? screen;

// Why the worker could not check an eRound screen, in plain words (SOPS-164).
const UNREACHABLE: Record<string, string> = {
  "login failed": "signing in to eRound failed",
  timeout: "eRound did not respond in time",
  "canary missing": "the test station in eRound is missing",
  "not reached": "the check did not get that far",
};

export const unreachableReason = (reason: string | null): string => {
  if (!reason) return "not every screen was reached";
  const blocked = /^blocked by (.+)$/.exec(reason);
  if (blocked) return `blocked by the change on ${screenTitle(blocked[1])}`;
  return UNREACHABLE[reason] ?? reason;
};
